import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  useBoardCreateMutation,
  BoardsDocument,
  type BoardsQueryData,
} from '../../../generated/graphql';

interface CreateBoardDialogProps {
  open: boolean;
  onClose: () => void;
}

export function CreateBoardDialog({ open, onClose }: CreateBoardDialogProps) {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) {
      setName('');
      setError(null);
    }
  }, [open]);
  const [createBoard, { loading }] = useBoardCreateMutation({
    update(cache, { data }) {
      if (!data) return;
      const prev = cache.readQuery<BoardsQueryData>({ query: BoardsDocument });
      if (!prev) return;
      cache.writeQuery<BoardsQueryData>({
        query: BoardsDocument,
        // Prepend so the new board appears first — matches the
        // newest-first ordering returned by a fresh `boards` query
        // refresh (avoids order flip between in-session create and
        // post-refresh).
        data: { boards: [data.boardCreate, ...prev.boards] },
      });
    },
  });

  const trimmed = name.trim();
  const canSubmit = trimmed.length > 0 && !loading;

  async function handleSubmit() {
    setError(null);
    try {
      const result = await createBoard({
        variables: { input: { name: trimmed } },
      });
      const id = result.data?.boardCreate._id;
      if (!id) throw new Error('missing id');
      setName('');
      onClose();
      navigate(`/boards/${id}`);
    } catch {
      setError("We couldn't create the board. Please try again.");
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Create board</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canSubmit) handleSubmit();
          }}
          inputProps={{ 'aria-label': 'name' }}
        />
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit}
          variant="contained"
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}
